import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Error Boundary
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: "center" }}>
          <h2 style={{ color: "#dc2626" }}>Something went wrong</h2>
          <p style={{ color: "#64748b", margin: "12px 0" }}>{this.state.error?.message}</p>
          <button onClick={() => { this.setState({ hasError: false }); window.location.href = "/"; }}
            style={{ padding: "8px 20px", background: "#4f46e5", color: "white", border: "none", borderRadius: 6, cursor: "pointer" }}>
            Go to Dashboard
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Layout Components
import { Layout } from "@/components/Layout";
import InstallPrompt from "@/components/InstallPrompt";

// Pages
import Dashboard from "@/pages/Dashboard";
import LeadsPage from "@/pages/LeadsPage";
import LeadDetailPage from "@/pages/LeadDetailPage";
import AutomationPage from "@/pages/AutomationPage";
import CalendarPage from "@/pages/CalendarPage";
import FormsPage from "@/pages/FormsPage";
import SettingsPage from "@/pages/SettingsPage";
import LoginPage from "@/pages/LoginPage";
import TeamPage from "@/pages/TeamPage";
import InvitePage from "@/pages/InvitePage";
import MySchedulePage from "@/pages/MySchedulePage";
import TasksPage from "@/pages/TasksPage";
import EstimatesPage from "@/pages/EstimatesPage";
import EstimateBuilder from "@/pages/EstimateBuilder";
import EstimateTemplatesPage from "@/pages/EstimateTemplatesPage";
import EstimateAcceptPage from "@/pages/EstimateAcceptPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import AutomationRulesPage from "@/pages/AutomationRulesPage";
import CustomerPortalPage from "@/pages/CustomerPortalPage";
import CustomerRequestsPage from "@/pages/CustomerRequestsPage";
import ProjectDocumentsPage from "@/pages/ProjectDocumentsPage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerDetailPage from "@/pages/CustomerDetailPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import SuppliersPage from "@/pages/SuppliersPage";
import OrdersPage from "@/pages/OrdersPage";
import JobCostingPage from "@/pages/JobCostingPage";
import ScorecardsPage from "@/pages/ScorecardsPage";
import MaterialsPage from "@/pages/MaterialsPage";
import ProcurementPage from "@/pages/ProcurementPage";
import ProductionSheetsPage from "@/pages/ProductionSheetsPage";
import ProductionSheetPage from "@/pages/ProductionSheetPage";
import PhotoGalleryPage from "@/pages/PhotoGalleryPage";
import TimecardPage from "@/pages/TimecardPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import ReviewsPage from "@/pages/ReviewsPage";
import ContractsPage from "@/pages/ContractsPage";
import ContractEditorPage from "@/pages/ContractEditorPage";
import InvoiceDetailPage from "@/pages/InvoiceDetailPage";
import ContractSignPage from "@/pages/ContractSignPage";
import PayPage from "@/pages/PayPage";
import ChatPage from "@/pages/ChatPage";
import CustomerChatPage from "@/pages/CustomerChatPage";
import PublicBidPage from "@/pages/PublicBidPage";
import CncRouterStationPage from "@/pages/CncRouterStationPage";
import AiDesignEstimateWizardPage from "@/pages/AiDesignEstimateWizardPage";
import QuickQuotePage from "@/pages/QuickQuotePage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Auth Context
export const AuthContext = React.createContext(null);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        // Check for invite token from URL params (set during invite accept flow)
        const urlParams = new URLSearchParams(window.location.search);
        const inviteToken = urlParams.get("invite_token");
        if (inviteToken) {
          // Clean the URL
          urlParams.delete("invite_token");
          const cleanUrl = window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
          window.history.replaceState({}, '', cleanUrl);
        }
        
        try {
          const response = await axios.post(`${API}/auth/session`, {
            session_id: sessionId,
            invite_token: inviteToken
          });
          
          if (response.data) {
            toast.success(`Welcome to Sage CRM!`);
            navigate("/dashboard", { state: { user: response.data }, replace: true });
          }
        } catch (error) {
          const detail = error.response?.data?.detail || error.message || "Unknown error";
          toast.error(`Authentication failed: ${detail}`);
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };

    processAuth();
  }, [navigate, location]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [loading, setLoading] = useState(!location.state?.user);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location.state]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const OwnerOnly = ({ children }) => {
  const { user } = React.useContext(AuthContext);
  return user?.role === "owner" ? children : <Navigate to="/dashboard" replace />;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/customer-chat/:token" element={<CustomerChatPage />} />
      
      <Route path="/" element={
        <ProtectedRoute>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="leads" element={<LeadsPage />} />
        <Route path="leads/:leadId" element={<LeadDetailPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="customers/:customerId" element={<CustomerDetailPage />} />
        <Route path="customers/:customerId/projects/:projectId" element={<ProjectDetailPage />} />
        <Route path="customers/:customerId/projects/:projectId/design-estimate" element={<OwnerOnly><AiDesignEstimateWizardPage /></OwnerOnly>} />
        <Route path="quick-quote" element={<OwnerOnly><QuickQuotePage /></OwnerOnly>} />
        <Route path="estimates" element={<EstimatesPage />} />
        <Route path="estimates/new" element={<EstimateBuilder />} />
        <Route path="estimates/templates" element={<EstimateTemplatesPage />} />
        <Route path="estimates/:estimateId" element={<EstimateBuilder />} />
        <Route path="automation" element={<AutomationPage />} />
        <Route path="automation-rules" element={<AutomationRulesPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="my-schedule" element={<MySchedulePage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="customer-requests" element={<CustomerRequestsPage />} />
        <Route path="projects/:projectId/documents" element={<ProjectDocumentsPage />} />
        <Route path="forms" element={<FormsPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="suppliers" element={<SuppliersPage />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="timecard" element={<TimecardPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="reviews" element={<ReviewsPage />} />
        <Route path="contracts" element={<ContractsPage />} />
        <Route path="contracts/:contractId" element={<ContractEditorPage />} />
        <Route path="invoices/:invoiceId" element={<InvoiceDetailPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="job-costing/:projectId" element={<JobCostingPage />} />
        <Route path="scorecards" element={<ScorecardsPage />} />
        <Route path="materials/:projectId" element={<MaterialsPage />} />
        <Route path="procurement" element={<ProcurementPage />} />
        <Route path="production-sheets" element={<ProductionSheetsPage />} />
        <Route path="production-sheets/:sheetId" element={<ProductionSheetPage />} />
        <Route path="cnc-router-station" element={<CncRouterStationPage />} />
        <Route path="photo-gallery/:projectId" element={<PhotoGalleryPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Public routes (no auth required) */}
      <Route path="/estimate/:estimateId" element={<EstimateAcceptPage />} />
      <Route path="/sign/:token" element={<ContractSignPage />} />
      <Route path="/sign-change/:token" element={<ContractSignPage kind="change" />} />
      <Route path="/pay/:payToken" element={<PayPage />} />
      <Route path="/portal/:accessToken" element={<CustomerPortalPage />} />
      <Route path="/shared/bids/:bidId" element={<PublicBidPage />} />

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <ErrorBoundary>
          <AppRouter />
        </ErrorBoundary>
      </BrowserRouter>
      <InstallPrompt />
    </div>
  );
}

export default App;
